import React from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';
import { translator } from '../assessments/crs/i18n';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  padding: 16px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  animation: ${fadeIn} 0.4s ease-out;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ScoreHeader = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const ScoreTitle = styled.h2`
  margin: 0 0 2px 0;
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332'};
`;

const ScoreSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d'};
`;

const GaugeContainer = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const ScoreNumber = styled.div`
  font-size: 38px;
  font-weight: 800;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332'};
  line-height: 1;
`;

const ScoreMax = styled.div`
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.4)' : '#6b7a8d'};
  margin-top: 2px;
`;

const ScoreMessage = styled.p`
  font-size: 12px;
  font-weight: 600;
  margin: 8px 0 0 0;
  text-align: center;
`;

const SectionCard = styled.div`
  background: ${p => p.$darkMode ? '#252525' : '#f8fafc'};
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const SectionLabel = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
`;

const SectionScore = styled.span`
  font-weight: 800;
  font-size: 16px;
`;

const SectionMax = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8'};
`;

const BarTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${p => p.$darkMode ? '#333' : '#e2e8f0'};
  margin-bottom: 8px;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${p => p.$color};
  width: ${p => Math.min((p.$points / p.$max) * 100, 100)}%;
  transition: width 0.6s ease;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.6)' : '#64748b'};
  padding: 2px 0;
`;

const DetailValue = styled.span`
  font-weight: 600;
`;

const CTACard = styled.div`
  margin-top: 20px;
  padding: 18px;
  border-radius: 14px;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode
      ? `linear-gradient(135deg, ${c.accentBgDark} 0%, ${c.bgCard} 100%)`
      : `linear-gradient(135deg, ${c.accentBg} 0%, #f0fdf4 100%)`;
  }};
  border: 1px solid ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode ? c.borderAccent : '#e2e8f0';
  }};
  text-align: center;
`;

const CTATitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332'};
`;

const CTADesc = styled.p`
  margin: 0 0 14px 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563'};
`;

const ContinueBtn = styled.button`
  width: 100%;
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(135deg, ${c.primary}, ${c.primaryDark})`;
  }};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  transition: all 0.2s;
  box-shadow: ${p => {
    const c = getColorScheme(p.$scheme);
    return `0 6px 18px ${c.shadowColor}`;
  }};

  &:hover {
    transform: translateY(-2px);
  }
`;

const DeclineBtn = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  border: 2px solid ${p => p.$darkMode ? '#444' : '#e2e8f0'};
  background: transparent;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.6)' : '#64748b'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  margin-top: 10px;
  transition: all 0.2s;

  &:hover { background: ${p => p.$darkMode ? '#333' : '#f1f5f9'}; }
`;

const DeclineNote = styled.p`
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8'};
  margin: 8px 0 0 0;
  text-align: center;
`;

const Disclaimer = styled.p`
  font-size: 10px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8'};
  text-align: center;
  line-height: 1.4;
  margin-top: 16px;
`;

const CRSResults = ({ result, onContinueToIntake, onDecline, siteMode = false, darkMode = false, scheme = 'blue', language = 'en' }) => {
  // Shared with CRSCalculator: one table, per-key English fallback, so the
  // twelve chrome-only locales render translated navigation and English copy
  // rather than blanks.
  const t = translator(language);
  const score = result.total;

  let color, message;
  if (score >= 500) { color = '#16a34a'; message = t('strong'); }
  else if (score >= 450) { color = '#d97706'; message = t('moderate'); }
  else if (score >= 400) { color = '#ea580c'; message = t('below'); }
  else { color = '#dc3545'; message = t('improve'); }

  // From November 3, 2026 Canadian work experience leaves the core factors and
  // becomes part of the Labour Market Integration factor (max 140 / 150).
  const lmi = result.labourMarket || null;
  const coreMax = lmi ? (result.hasSpouse ? 390 : 420) : (result.hasSpouse ? 460 : 500);
  const maxTotal = result.maxScore || 1200;
  const pct = Math.min((score / maxTotal) * 100, 100);

  const sections = [
    {
      label: t('coreLabel'), points: result.core.total,
      max: coreMax, color: getColorScheme(scheme).primary,
      details: [
        { label: t('ageLabel'), points: result.core.age },
        { label: t('educationLabel'), points: result.core.education },
        { label: t('firstLangLabel'), points: result.core.firstLang },
        { label: t('secondLangLabel'), points: result.core.secondLang },
        ...(lmi ? [] : [{ label: t('canWorkLabel'), points: result.core.canadianWork }]),
      ]
    },
    ...(lmi ? [{
      label: t('lmiLabel'), points: lmi.total, max: lmi.max, color: '#2c58e9',
      details: [
        { label: t('lmiWorkLengthLabel'), points: lmi.workLength },
        { label: t('lmiHighWageLabel'), points: lmi.highWage },
        { label: t('lmiJobOfferLabel'), points: lmi.jobOffer },
        { label: lmi.used === 'jobOffer' ? t('lmiUsedOffer') : t('lmiUsedWork'), points: lmi.total },
      ]
    }] : []),
    ...(result.spouse?.total > 0 ? [{
      label: t('spouseLabel'), points: result.spouse.total, max: 40, color: '#7c3aed',
      details: [
        { label: t('spouseEduLabel'), points: result.spouse.education },
        { label: t('spouseLangLabel'), points: result.spouse.language },
        { label: t('spouseWorkLabel'), points: result.spouse.work },
      ]
    }] : []),
    { label: t('skillLabel'), points: result.skill, max: 100, color: '#0891b2' },
    { label: t('additionalLabel'), points: result.additional, max: 600, color: '#16a34a' },
  ];

  return (
    <Wrapper>
      <ScoreHeader>
        <ScoreTitle $darkMode={darkMode}>{t('title')}</ScoreTitle>
        <ScoreSubtitle $darkMode={darkMode}>{t('subtitle')}</ScoreSubtitle>
      </ScoreHeader>

      <GaugeContainer>
        <div style={{ position: 'relative', width: 160, height: 160, margin: '0 auto 8px' }}>
          <svg viewBox="0 0 200 200" style={{ width: 160, height: 160 }}>
            <circle cx="100" cy="100" r="85" fill="none" stroke={darkMode ? '#333' : '#f1f5f9'} strokeWidth="12" />
            <circle cx="100" cy="100" r="85" fill="none" stroke={color} strokeWidth="12"
              strokeDasharray={`${pct * 5.34} 534`} strokeDashoffset="0"
              strokeLinecap="round" transform="rotate(-90 100 100)"
              style={{ transition: 'stroke-dasharray 1s ease' }}
            />
          </svg>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
            <ScoreNumber $darkMode={darkMode}>{score}</ScoreNumber>
            <ScoreMax $darkMode={darkMode}>/ {maxTotal.toLocaleString('en-US')}</ScoreMax>
          </div>
        </div>
        <ScoreMessage style={{ color }}>{message}</ScoreMessage>
      </GaugeContainer>

      {sections.map((s, i) => (
        <SectionCard key={i} $darkMode={darkMode}>
          <SectionHeader>
            <SectionLabel $darkMode={darkMode}>{s.label}</SectionLabel>
            <SectionScore style={{ color: s.color }}>
              {s.points}<SectionMax $darkMode={darkMode}>/{s.max}</SectionMax>
            </SectionScore>
          </SectionHeader>
          <BarTrack $darkMode={darkMode}>
            <BarFill $color={s.color} $points={s.points} $max={s.max} />
          </BarTrack>
          {s.details && s.details.map((d, j) => (
            <DetailRow key={j} $darkMode={darkMode}>
              <span>{d.label}</span>
              <DetailValue>{d.points}</DetailValue>
            </DetailRow>
          ))}
        </SectionCard>
      ))}

      <CTACard $darkMode={darkMode} $scheme={scheme}>
        {/* On imavenai.com the next step is iMaven's AI program check, not a consultant. */}
        <CTATitle $darkMode={darkMode}>{t(siteMode ? 'siteTitle' : 'bonusTitle')}</CTATitle>
        <CTADesc $darkMode={darkMode}>{t(siteMode ? 'siteDesc' : 'bonusDesc')}</CTADesc>
        <ContinueBtn $scheme={scheme} onClick={onContinueToIntake}>
          {t(siteMode ? 'siteContinue' : 'continueBtn')}
        </ContinueBtn>
        {onDecline && (<>
          <DeclineBtn $darkMode={darkMode} onClick={onDecline}>
            {t('declineBtn')}
          </DeclineBtn>
          <DeclineNote $darkMode={darkMode}>{t('declineNote')}</DeclineNote>
        </>)}
      </CTACard>

      <Disclaimer $darkMode={darkMode}>{t('disclaimer')}</Disclaimer>
    </Wrapper>
  );
};

export default CRSResults;
